import { z } from 'zod/v4';

export const InternalRoleRuntype = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
    zed_token: z.string().nullable(),
});

export type InternalRole = z.infer<typeof InternalRoleRuntype>;

// Module enum to match the database 'module_enum' type
export const ModuleEnum = z.enum([
    'rfqs',
    'design',
    'sourcing',
    'manufacturing',
    'calculation',
    'quotation',
    'customers',
    'organization_settings',
    'customer_portal',
    'supplier_portal',
    'pcb_pricing',
    'pcb_quotation',
]);

export type Module = z.infer<typeof ModuleEnum>;
const internalModules = [
    'rfqs',
    'design',
    'sourcing',
    'manufacturing',
    'calculation',
    'quotation',
    'customers',
    'organization_settings',
    'pcb_pricing',
    'pcb_quotation',
] as const satisfies ReadonlyArray<Module>; // <-- compile-time check here

// Then create the Zod enum from the subset
export const InternalModuleEnum = z.enum(internalModules);
export type InternalModule = z.infer<typeof InternalModuleEnum>;

export function isInternalModule(value: string): value is InternalModule {
    return InternalModuleEnum.safeParse(value).success;
}

// RoleAccessLevel enum to match the database 'role_access_level_enum' type
export const RoleAccessLevelEnum = z.enum(['viewer', 'editor', 'manager']);

export type RoleAccessLevel = z.infer<typeof RoleAccessLevelEnum>;

// InternalRoleAccess runtype to match the 'internal_role_access' table
export const InternalRoleAccessRuntype = z.object({
    internal_role_id: z.string(),
    internal_module: InternalModuleEnum,
    access_level: RoleAccessLevelEnum,
});

export type InternalRoleAccess = z.infer<typeof InternalRoleAccessRuntype>;

// Create internal role request runtype
export const CreateInternalRoleRuntype = z.object({
    name: z.string(),
    description: z.string().nullable(),
});

export type CreateInternalRole = z.infer<typeof CreateInternalRoleRuntype>;

// Update internal role request runtype
export const UpdateInternalRoleRuntype = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string().nullable(),
});

export type UpdateInternalRole = z.infer<typeof UpdateInternalRoleRuntype>;

export const CreateInternalRoleAccessRuntype = InternalRoleAccessRuntype;

export const DeleteInternalRoleAccessRuntype = z.object({
    internal_role_id: z.string(),
    internal_module: InternalModuleEnum,
});

const PermissionKindEnum = z.enum(['view', 'edit', 'manage']);
export type PermissionKind = z.infer<typeof PermissionKindEnum>;

export const ModulePermissionRuntype = z.object({
    module: ModuleEnum,
    permission: PermissionKindEnum,
});
export type ModulePermission = z.infer<typeof ModulePermissionRuntype>;

export const InternalUserRoleMembershipInsertRuntype = z.object({
    user_id: z.string(),
    internal_role_id: z.string(),
});
export type InternalUserRoleMembershipInsert = z.infer<typeof InternalUserRoleMembershipInsertRuntype>;

export const InternalUserRoleMembershipRuntype = InternalUserRoleMembershipInsertRuntype.extend({
    updated_at: z.string().datetime(),
});
export type InternalUserRoleMembership = z.infer<typeof InternalUserRoleMembershipRuntype>;
